import { useMemo, useState } from 'react';
import { Checkbox, Input } from '../ui/index.ts';

export interface SkuPickerSku {
  readonly sku_id: string;
  readonly sku_name: string;
}

/** Prefix before the first `/`. Empty string means the "other" bucket. */
export function skuNamespaceKey(skuName: string): string {
  const slash = skuName.indexOf('/');
  return slash > 0 ? skuName.slice(0, slash) : '';
}

export interface SkuPickerGroup {
  readonly key: string;
  readonly label: string;
  readonly skus: readonly SkuPickerSku[];
}

/**
 * Groups SKUs by namespace. A query matches the group label (every SKU in
 * the group stays) or an individual SKU name / id.
 */
export function groupSkusForPicker(
  skus: readonly SkuPickerSku[],
  query: string,
  otherLabel: string,
): readonly SkuPickerGroup[] {
  const q = query.trim().toLowerCase();
  const buckets = new Map<string, SkuPickerSku[]>();
  for (const sku of skus) {
    const key = skuNamespaceKey(sku.sku_name);
    const existing = buckets.get(key);
    buckets.set(key, existing === undefined ? [sku] : [...existing, sku]);
  }
  const keys = [...buckets.keys()].sort((left, right) => {
    if (left === '') return 1;
    if (right === '') return -1;
    return left.localeCompare(right);
  });
  const groups: SkuPickerGroup[] = [];
  for (const key of keys) {
    const all = buckets.get(key) ?? [];
    const label = key === '' ? otherLabel : key;
    const groupMatches = q.length > 0 && label.toLowerCase().includes(q);
    const shown = q.length === 0 || groupMatches
      ? all
      : all.filter((sku) =>
        sku.sku_name.toLowerCase().includes(q) || sku.sku_id.toLowerCase().includes(q));
    if (shown.length === 0) continue;
    groups.push({ key: key === '' ? 'other' : key, label, skus: shown });
  }
  return groups;
}

function withIds(selected: ReadonlySet<string>, ids: readonly string[], present: boolean): ReadonlySet<string> {
  const next = new Set(selected);
  for (const id of ids) {
    if (present) next.add(id);
    else next.delete(id);
  }
  return next;
}

export function SkuGroupPicker({
  skus,
  selectedIds,
  onChange,
  otherLabel,
  placeholder,
  filterLabel,
  listTestId = 'sku-group-picker',
  filterTestId = 'sku-group-picker-filter',
}: {
  readonly skus: readonly SkuPickerSku[];
  readonly selectedIds: ReadonlySet<string>;
  readonly onChange: (next: ReadonlySet<string>) => void;
  readonly otherLabel: string;
  readonly placeholder: string;
  readonly filterLabel?: string;
  readonly listTestId?: string;
  readonly filterTestId?: string;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const groups = useMemo(
    () => groupSkusForPicker(skus, query, otherLabel),
    [skus, query, otherLabel],
  );

  return (
    <div className="flex flex-col gap-2">
      {filterLabel === undefined ? null : (
        <label htmlFor={filterTestId} className="text-label text-paper-mid">{filterLabel}</label>
      )}
      <Input
        id={filterTestId}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        placeholder={placeholder}
        aria-label={filterLabel ?? placeholder}
        data-testid={filterTestId}
      />
      <div className="flex max-h-64 flex-col gap-2 overflow-auto" data-testid={listTestId}>
        {groups.map((group) => {
          const ids = group.skus.map((sku) => sku.sku_id);
          const selectedCount = ids.filter((id) => selectedIds.has(id)).length;
          const all = ids.length > 0 && selectedCount === ids.length;
          const partial = selectedCount > 0 && !all;
          const open = !collapsed.has(group.key);
          return (
            <div key={group.key} className="rounded border border-line-soft bg-card" data-testid={`sku-group-${group.key}`}>
              <div className="flex items-center gap-2 px-2 py-1.5">
                <Checkbox
                  checked={all}
                  indeterminate={partial}
                  onCheckedChange={() => onChange(withIds(selectedIds, ids, !all))}
                  testId={`sku-group-toggle-${group.key}`}
                />
                <button
                  type="button"
                  className="flex-1 text-left text-dense text-paper"
                  aria-expanded={open}
                  data-testid={`sku-group-collapse-${group.key}`}
                  onClick={() => {
                    const next = new Set(collapsed);
                    if (open) next.add(group.key);
                    else next.delete(group.key);
                    setCollapsed(next);
                  }}
                >
                  {group.label}
                </button>
              </div>
              {open ? (
                <div className="flex flex-col gap-1 border-t border-line-soft px-2 py-1.5">
                  {group.skus.map((sku) => (
                    <label key={sku.sku_id} className="flex items-center gap-2 text-dense text-paper">
                      <Checkbox
                        checked={selectedIds.has(sku.sku_id)}
                        onCheckedChange={(next) => onChange(withIds(selectedIds, [sku.sku_id], next))}
                        testId={`sku-group-sku-${sku.sku_id}`}
                      />
                      {sku.sku_name}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
